// 导入React框架及状态管理工具
import React from 'react'

// 导入图标库Lucide的图标组件（可从https://lucide.dev/icons/查看更多图标）
import { Home, Film, List, Wrench, Settings, User, LogOut, ArrowLeft, Workflow } from 'lucide-react'

// 导入路由相关工具函数，用于页面导航和路径获取
import { useLocation, useNavigate } from 'react-router-dom'

// 导入导航配置数据，包含所有导航菜单和子菜单的结构与信息
import { navigationConfig } from '@/utils/navigation'

// 导入工具函数，用于合并CSS类名
import { cn } from '@/utils'
import { logoutApi } from '@/utils/api'
import axios from '@/utils/api'
// 导入国际化钩子
import { useTranslation } from '@/i18n'

// 侧边栏组件的核心实现
const EditSidebar: React.FC = () => {
  // 路由相关钩子，用于获取当前页面的路径信息
  const location = useLocation()

  // 路由导航钩子，用于编程式跳转到其他页面
  const navigate = useNavigate()

  // 国际化钩子
  const { t } = useTranslation()

  // ====================== 辅助函数 ======================
  // 根据图标名称获取对应的图标组件
  // 参数:
  // - iconName: 图标名称字符串 (与导航配置中的 icon 字段对应)
  // - noMargin: 布尔值，是否去掉图标右边距 (用于折叠状态下的图标居中)
  const getIconComponent = (iconName: string, noMargin = false) => {
    // 设置图标样式类名
    // - 折叠状态下大小为 w-6 h-6 (24px x 24px)，居中显示
    const className = noMargin ? "w-6 h-6 opacity-80" : "w-4 h-4 mr-2 opacity-80"

    // 根据图标名称返回对应的 Lucide React 图标组件
    switch (iconName) {
      // 主页图标
      case 'Home':
        return <Home className={className} />
      // 视频图标
      case 'Film':
        return <Film className={className} />
      // 列表图标
      case 'List':
        return <List className={className} />
      // 工具图标
      case 'Tools':
        return <Wrench className={className} />
      // 设置图标
      case 'Settings':
        return <Settings className={className} />
      // 用户图标
      case 'User':
        return <User className={className} />
      // 登出图标
      case 'LogOut':
        return <LogOut className={className} />
      // 工作流图标
      case 'Workflow':
        return <Workflow className={className} />
      default:
        return null // 如果没有匹配到图标，则返回 null (避免显示错误)
    }
  }

  // 判断某个导航项是否处于活跃状态
  // 参数: path - 导航项指向的页面路径
  // 返回: 布尔值，如果当前页面路径与导航项路径一致则返回true
  const isActive = (path: string) => {
    // 比较当前页面的路径 (location.pathname) 和导航项的路径
    return location.pathname === path
  }

  // 处理导航点击事件
  // 参数: path - 要导航到的页面路径
  const handleNavigation = (path: string) => {
    if (path === '/logout') {
      // 处理登出逻辑
      logoutApi()
        .then(() => {
          // 清除本地存储
          localStorage.removeItem('token')
          localStorage.removeItem('user')

          // 清除axios默认的Authorization头
          axios.defaults.headers.common['Authorization'] = ''

          // 导航到登录页面
          navigate('/login', {
            state: { message: t('login.logout.success') }
          })
        })
        .catch((error) => {
          console.error('Logout error:', error)

          // 即使API调用失败，也清除本地存储并导航到登录页面
          localStorage.removeItem('token')
          localStorage.removeItem('user')

          // 清除axios默认的Authorization头
          axios.defaults.headers.common['Authorization'] = ''
          navigate('/login')
        })
    } else {
      // 正常导航逻辑
      navigate(path)
    }
  }

  return (
    <div className="bg-gradient-to-b from-gray-950/98 via-gray-950/95 to-gray-900/90 backdrop-blur-lg border-r border-gray-800/80 flex flex-col h-full transition-all duration-300 ease-in-out w-14">
      {/* Logo 区域 - 修改为返回按钮 */}
      <div className="p-0 min-h-[64px] flex items-center">
        {/* 折叠状态：返回按钮 */}
        <div className="flex justify-center w-full">
          <button
            className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-md flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 scale-105"
            onClick={() => navigate(-1)} // 返回上一页
            title="返回上一页"
          >
            <ArrowLeft className="text-white w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 导航菜单区域 */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 flex flex-col items-center">
        {/* 折叠状态：仅显示图标，居中排列 */}
        {navigationConfig.map((item) => (
          <div
            key={item.id}
            className={cn(
              // 基础样式：
              // - flex items-center justify-center：图标居中
              // - px-2 py-3：内边距
              // - my-1：垂直外边距
              // - w-8 h-8：固定宽高，确保图标大小一致
              // - mx-auto：水平居中
              // - rounded-lg：圆角
              // - cursor-pointer：鼠标指针
              // - transition-all：平滑过渡
              // - text-gray-500：默认文字颜色
              // - hover:text-white：悬停时文字变白
              // - hover:bg-white/10：悬停时背景色变浅
              'flex items-center justify-center my-1 w-8 h-8 mx-auto rounded-lg cursor-pointer transition-all duration-300 text-gray-500 hover:text-white hover:bg-white/10',

              // 激活状态样式：
              // - bg-white/10：背景色变浅
              // - text-white：文字变白
              isActive(item.path) && 'bg-white/10 text-white'
            )}
            // 点击时导航：
            // - 如果有子菜单，则导航到第一个子菜单的路径
            // - 如果没有子菜单，则直接导航到当前项的路径
            onClick={() => handleNavigation(item.children ? item.children[0].path : item.path)}
          >
            {getIconComponent(item.icon, true)} {/* 获取并渲染图标，noMargin=true 确保图标居中 */}
          </div>
        ))}
      </nav>
    </div>
  )
}

export default EditSidebar